import type { ContractEffort, EffortBurnPoint } from '@forecastify/shared';
import { LineChart, type LineSeries } from '@forecastify/ui';
import { useT, type Translate } from '../../../i18n';
import { FinanceTip } from '../finance/finance-tip';
import { useHourFormat } from './effort-format';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · BURN-DOWN AND BURN-UP, SIDE BY SIDE, IN HOURS.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The server draws nothing; it hands one point per column (`effortBurnSchema` argues each line).
 *
 * - **Burn-up** — *Scope* (BAC), *Plan* (PV, the allocation) and *Done* (EV, weighted by status).
 *   Done under Plan is behind schedule; a step in Scope is scope added.
 * - **Burn-down** — *Remaining* (scope − done), *Remaining by plan*, the straight *Ideal* and the
 *   dashed *Forecast*: what is left at the pace work has actually been done so far.
 *
 * ⚠️ **A retainer burns its FRAME** — the picked periods' hours, the hours that consumed them —
 * so its lines are named for a frame rather than for tasks.
 */
const DONE = 'var(--primary)';
const PLAN = 'var(--id-2)';
const NEUTRAL = 'var(--muted-foreground)';

/**
 * Five ticks on whole units. ⚠️ 3 Nov 2026 · `unit` is the hours in one of the reader's units — `8`
 * in man-days, so a tick reads `5 nap` rather than `4,63 nap` (`decisions.md` DU-1).
 */
function scaleOf(values: readonly number[], unit: number): { max: number; ticks: number[] } {
  const raw = Math.max(1, ...values) * 1.08;
  const step = Math.ceil(raw / 4 / unit) * unit;
  return { max: step * 4, ticks: [0, step, step * 2, step * 3, step * 4] };
}

export function EffortBurnCards({ view }: { readonly view: ContractEffort }) {
  const { hours, step: unitHours } = useHourFormat();
  const t = useT();
  const burn = view.burn;
  if (burn === null || burn.points.length === 0) return null;
  const frame = burn.kind === 'frame';
  /* At most eight labels — a column's label is a date span, and half a card is narrow. */
  const every = Math.max(1, Math.ceil(burn.points.length / 8));
  const todayIndex = burn.points.findIndex((point) => point.to >= view.today);
  const n = (value: string | null): number | null => (value === null ? null : Number(value));

  const up: LineSeries[] = [
    {
      key: 'scope',
      label: frame ? t('effort.burn.frame') : t('effort.burn.scope'),
      color: NEUTRAL,
    },
    { key: 'plan', label: t('effort.burn.plan'), color: PLAN, dashed: true },
    /* 30 Oct 2026 · the even pace beside the plan — the reference's straight *To Do*. */
    { key: 'straight', label: t('effort.burn.ideal'), color: NEUTRAL, dashed: true },
    { key: 'done', label: frame ? t('effort.burn.used') : t('effort.burn.done'), color: DONE },
  ];
  const down: LineSeries[] = [
    { key: 'remaining', label: t('effort.burn.remaining'), color: DONE },
    { key: 'plannedRemaining', label: t('effort.burn.plannedRemaining'), color: PLAN },
    { key: 'ideal', label: t('effort.burn.ideal'), color: NEUTRAL, dashed: true },
    { key: 'forecast', label: t('effort.burn.forecast'), color: DONE, dashed: true },
  ];

  const tooltip = (
    point: EffortBurnPoint,
    shown: readonly LineSeries[],
    values: Record<string, number | null>,
  ) => (
    <>
      <div className="font-medium">{point.label}</div>
      {point.future ? (
        <div className="text-muted-foreground text-[11px]">{t('effort.burn.ahead')}</div>
      ) : null}
      <div className="mt-1 flex flex-col gap-1">
        {shown.map((one) => {
          const value = values[one.key];
          return value === null || value === undefined ? null : (
            <div key={one.key} className="flex items-center gap-2">
              <span
                className="h-0.5 w-3 shrink-0 rounded-full"
                style={{ background: one.color }}
                aria-hidden
              />
              <span className="text-muted-foreground min-w-0 flex-1 truncate">{one.label}</span>
              <span className="tabular-nums">{hours(value)}</span>
            </div>
          );
        })}
      </div>
    </>
  );

  const card = (kind: 'up' | 'down') => {
    const shown = kind === 'up' ? up : down;
    const points = burn.points.map((point, index) => {
      const values: Record<string, number | null> =
        kind === 'up'
          ? {
              scope: n(point.scope),
              plan: n(point.plan),
              /* The burn-down's ideal, turned up: the budget less what an even pace leaves. */
              straight: Number(point.scope) - Number(point.ideal),
              done: n(point.done),
            }
          : {
              remaining: n(point.remaining),
              plannedRemaining: n(point.plannedRemaining),
              ideal: n(point.ideal),
              forecast: n(point.forecast),
            };
      return {
        key: point.key,
        /* A day or week column's label is a date span; its last day, as month-day, fits. */
        label: index % every !== 0 ? '' : point.label.length <= 8 ? point.label : point.to.slice(5),
        values,
        tooltip: tooltip(point, shown, values),
      };
    });
    const scale = scaleOf(
      points.flatMap((point) =>
        Object.values(point.values).filter((value): value is number => value !== null),
      ),
      unitHours,
    );
    return (
      <section className="bg-card min-w-0 rounded-xl border p-4 shadow-sm">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          <FinanceTip calcLabel={t('finance.calc')} tip={tipOf(t, kind, frame)}>
            {kind === 'up' ? t('effort.burn.upTitle') : t('effort.burn.downTitle')}
          </FinanceTip>
        </div>
        <LineChart
          className="mt-4"
          series={shown}
          points={points}
          max={scale.max}
          ticks={scale.ticks.map((value) => ({ value, label: hours(value) }))}
          {...(todayIndex >= 0 ? { todayIndex, todayLabel: t('effort.burn.today') } : {})}
          label={kind === 'up' ? t('effort.burn.upTitle') : t('effort.burn.downTitle')}
        />
        {burn.historyFrom === null || kind === 'down' ? null : (
          <p className="text-muted-foreground mt-2 text-[11px]">
            {t('effort.burn.historyFrom', { date: burn.historyFrom })}
          </p>
        )}
      </section>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {card('down')}
      {card('up')}
    </div>
  );
}

function tipOf(t: Translate, kind: 'up' | 'down', frame: boolean) {
  if (kind === 'up') {
    return {
      name: t('effort.burn.upTitle'),
      what: frame ? t('effort.burn.upWhatFrame') : t('effort.burn.upWhat'),
      calc: t('effort.burn.upCalc'),
    };
  }
  return {
    name: t('effort.burn.downTitle'),
    what: t('effort.burn.downWhat'),
    calc: t('effort.burn.downCalc'),
  };
}
